import { Controller } from "@hotwired/stimulus"
import { Calendar } from '@fullcalendar/core'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'

export default class extends Controller {
  static targets = ["container", "modal", "periodLabel", "taskToggle", "overdueToggle", "contentToggle",
                     "taskCount", "overdueCount", "contentCount"]
  static values = {
    eventsUrl: String,
    prefsUrl: String,
    googleCalendars: Array,
    initialDate: String,
    initialView: { type: String, default: "dayGridMonth" },
    selectedCalendars: Array,
    showTasks: { type: Boolean, default: true },
    showOverdue: { type: Boolean, default: true },
    showContent: { type: Boolean, default: true },
    firstVisit: { type: Boolean, default: false }
  }

  connect() {
    this.selectedCalendarIds = [...this.selectedCalendarsValue]
    // First visit: select ALL Google calendars
    if (this.firstVisitValue && this.googleCalendarsValue.length > 0) {
      this.selectedCalendarIds = this.googleCalendarsValue.map(c => c.id)
    }
    this.calendarColors = {}
    this.googleCalendarsValue.forEach(cal => {
      this.calendarColors[cal.id] = cal.color
    })
    this.updateToggleStyles()
    this.initCalendar()
  }

  normalizeView(view) {
    const valid = ['dayGridMonth', 'timeGridWeek', 'timeGridDay']
    if (valid.includes(view)) return view
    // Map legacy view names
    const map = { week: 'timeGridWeek', month: 'dayGridMonth', day: 'timeGridDay' }
    return map[view] || 'dayGridMonth'
  }

  initCalendar() {
    try {
      this.calendar = new Calendar(this.containerTarget, {
        plugins: [dayGridPlugin, timeGridPlugin],
        initialView: this.normalizeView(this.initialViewValue),
        initialDate: this.initialDateValue || new Date(),
        scrollTime: '09:00:00',
        slotMinTime: '00:00:00',
        slotMaxTime: '24:00:00',
        headerToolbar: {
          left: 'prev,next today',
          center: 'title',
          right: 'dayGridMonth,timeGridWeek,timeGridDay'
        },
        height: '75vh',
        nowIndicator: true,
        events: this.fetchEvents.bind(this),
        eventClick: this.handleEventClick.bind(this),
        datesSet: this.handleDatesSet.bind(this),
        viewDidMount: (info) => {
          this.savePreference('default_view', info.view.type)
        },
        eventDidMount: (info) => {
          const type = info.event.extendedProps.type
          if (type === 'google') {
            const calId = info.event.extendedProps.calendarId
            if (calId && this.calendarColors[calId]) {
              info.el.style.backgroundColor = this.calendarColors[calId]
              info.el.style.borderColor = this.calendarColors[calId]
            }
          }
          info.el.style.cursor = 'pointer'
        }
      })

      this.calendar.render()
    } catch (error) {
      console.error('FullCalendar init failed:', error)
      this.containerTarget.innerHTML = `<p class="text-red-500 text-sm p-4">Calendar failed to load: ${error.message}</p>`
    }
  }

  fetchEvents(info, successCallback, failureCallback) {
    const params = new URLSearchParams({
      start: info.startStr,
      end: info.endStr,
      show_tasks: this.showTasksValue,
      show_overdue: this.showOverdueValue,
      show_content: this.showContentValue
    })

    this.selectedCalendarIds.forEach(id => {
      params.append('calendar_ids[]', id)
    })

    fetch(`${this.eventsUrlValue}?${params}`, {
      headers: { 'Accept': 'application/json' }
    })
    .then(response => response.json())
    .then(events => {
      successCallback(events)
      this.updateCounts(events, info)
    })
    .catch(error => {
      console.error('Failed to fetch events:', error)
      failureCallback(error)
    })
  }

  handleDatesSet(info) {
    const view = info.view
    let label = ''
    const start = view.currentStart
    const end = view.currentEnd

    if (view.type === 'dayGridMonth') {
      label = start.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    } else if (view.type === 'timeGridWeek') {
      const endDate = new Date(end)
      endDate.setDate(endDate.getDate() - 1)
      label = `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
    } else {
      label = start.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })
    }

    if (this.hasPeriodLabelTarget) {
      this.periodLabelTarget.textContent = label
    }
  }

  updateCounts(events, info) {
    let taskCount = 0, overdueCount = 0, contentCount = 0

    events.forEach(e => {
      const type = e.extendedProps?.type
      if (type === 'task') taskCount++
      else if (type === 'overdue') overdueCount++
      else if (type === 'content') contentCount++
    })

    if (this.hasTaskCountTarget) this.taskCountTarget.textContent = taskCount
    if (this.hasOverdueCountTarget) this.overdueCountTarget.textContent = overdueCount
    if (this.hasContentCountTarget) this.contentCountTarget.textContent = contentCount
  }

  toggleSource(event) {
    const source = event.currentTarget.dataset.source
    const checked = event.currentTarget.checked

    if (source === 'tasks') {
      this.showTasksValue = checked
      this.savePreference('show_tasks', checked)
    } else if (source === 'overdue') {
      this.showOverdueValue = checked
      this.savePreference('show_overdue', checked)
    } else if (source === 'content') {
      this.showContentValue = checked
      this.savePreference('show_content', checked)
    }

    this.updateToggleStyles()
    this.calendar.refetchEvents()
  }

  updateToggleStyles() {
    if (this.hasTaskToggleTarget) {
      this.taskToggleTarget.style.opacity = this.showTasksValue ? '1' : '0.4'
    }
    if (this.hasOverdueToggleTarget) {
      this.overdueToggleTarget.style.opacity = this.showOverdueValue ? '1' : '0.4'
    }
    if (this.hasContentToggleTarget) {
      this.contentToggleTarget.style.opacity = this.showContentValue ? '1' : '0.4'
    }
  }

  toggleCalendar(event) {
    const calendarId = event.target.value
    if (event.target.checked) {
      if (!this.selectedCalendarIds.includes(calendarId)) {
        this.selectedCalendarIds.push(calendarId)
      }
    } else {
      this.selectedCalendarIds = this.selectedCalendarIds.filter(id => id !== calendarId)
    }
    this.savePreference('selected_calendars', this.selectedCalendarIds)
    this.calendar.refetchEvents()
  }

  handleEventClick(info) {
    const props = info.event.extendedProps

    if (props.type === 'task' || props.type === 'overdue' || props.type === 'content') {
      if (props.url) {
        window.location.href = props.url
        return
      }
    }

    if (props.type === 'google') {
      this.showEventModal(info.event)
    }
  }

  showEventModal(event) {
    const modal = this.modalTarget
    const props = event.extendedProps

    let startStr = ''
    if (event.allDay) {
      startStr = event.start ? event.start.toLocaleDateString() : ''
    } else {
      startStr = event.start ? event.start.toLocaleString() : ''
    }

    let endStr = ''
    if (event.end) {
      endStr = event.allDay ? event.end.toLocaleDateString() : event.end.toLocaleString()
    }

    let html = `
      <div class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center" data-action="click->bmt-calendar#closeModal">
        <div class="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6" data-action="click->bmt-calendar#stopPropagation">
          <div class="flex items-start justify-between mb-4">
            <h3 class="text-lg font-semibold text-[#2D2D2D]">${this.escapeHtml(event.title)}</h3>
            <button data-action="click->bmt-calendar#closeModal" class="text-[#8C8C8C] hover:text-[#2D2D2D]">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
          <div class="space-y-3 text-sm">
            <div class="flex items-center text-[#8C8C8C]">
              <svg class="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
              <span>${startStr}${endStr ? ' - ' + endStr : ''}</span>
            </div>
            ${props.location ? `
              <div class="flex items-center text-[#8C8C8C]">
                <svg class="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                </svg>
                <span>${this.escapeHtml(props.location)}</span>
              </div>
            ` : ''}
            ${props.description ? `
              <div class="mt-3 text-[#2D2D2D] whitespace-pre-wrap border-t border-[#E8E4DF] pt-3">
                ${this.escapeHtml(props.description).substring(0, 500)}
              </div>
            ` : ''}
            ${props.htmlLink ? `
              <div class="mt-3 pt-3 border-t border-[#E8E4DF]">
                <a href="${props.htmlLink}" target="_blank" rel="noopener" class="text-[#C4A484] hover:underline text-sm">
                  Open in Google Calendar
                </a>
              </div>
            ` : ''}
          </div>
        </div>
      </div>
    `

    modal.innerHTML = html
    modal.classList.remove('hidden')
  }

  closeModal() {
    this.modalTarget.classList.add('hidden')
    this.modalTarget.innerHTML = ''
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  savePreference(key, value) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    if (!csrfToken || !this.prefsUrlValue) return

    const body = {}
    body[key] = value

    fetch(this.prefsUrlValue, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken,
        'Accept': 'application/json'
      },
      body: JSON.stringify(body)
    }).catch(err => console.warn('Failed to save preference:', err))
  }

  escapeHtml(str) {
    if (!str) return ''
    const div = document.createElement('div')
    div.textContent = str
    return div.innerHTML
  }
}
